/**
 * 批次管理列表页
 *
 * 功能：
 *   - 展示所有批次（按 batch_number + vendor_id + standard_cost_type 聚合）
 *   - 每行显示：批次号、供应商、费用类型、机场、IDS 数量、处理进度、时间
 *   - 新建批次（生成 batch_number → 跳转工作台）
 *   - 点击行 → 跳转工作台
 *   - 筛选：供应商/费用类型/机场（后端 API 支持） + 批次号搜索（quickControls）
 *
 * 路由：/settlement/ap-batches
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Table, Button, Tag, Card, Typography, Input, Progress, Tooltip, message, Empty, Space } from "antd";
import { PlusOutlined, SearchOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, ExclamationCircleOutlined, StopOutlined } from "@ant-design/icons";

import settlementApi from "../../../api/settlementApi";
import { getStandardCostTypeLabel, STANDARD_COST_TYPE_LABELS } from "../constants";
import CollapsibleFilterBar from "../../../components/common/CollapsibleFilterBar/CollapsibleFilterBar";
import { contractManagementService } from "../../../api/enhancedApi";
import { useAuth } from "../../../hooks/useAuth";
import UploadDrawer from "./components/upload/UploadDrawer";
import ApInvBatchFilter from "./components/ApInvBatchFilter";

const { Text } = Typography;

/**
 * 状态进度条 — 四档指示
 */
const BatchProgressBar = ({ statusSummary, total }) => {
    if (!total) return <Text type="secondary">—</Text>;

    const { processing = 0, failed = 0, review = 0, completed = 0, voided = 0 } = statusSummary || {};
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

    // 有失败时红色，有处理中时蓝色动态，否则正常
    let status = undefined;
    let strokeColor = "#52c41a";
    if (failed > 0) {
        strokeColor = "#ff4d4f";
        status = "exception";
    } else if (processing > 0) {
        strokeColor = "#1890ff";
        status = "active";
    } else if (percent < 100) {
        strokeColor = "#faad14";
    }

    return (
        <Tooltip
            title={
                <div>
                    <div>处理中: {processing}</div>
                    <div>失败: {failed}</div>
                    <div>待审核: {review}</div>
                    <div>已完成: {completed}</div>
                    {voided > 0 && <div>已作废: {voided}</div>}
                </div>
            }
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Progress percent={percent} size="small" showInfo={false} strokeColor={strokeColor} status={status} style={{ width: 80, margin: 0 }} />
                <span style={{ fontSize: 12, color: "#8c8c8c", whiteSpace: "nowrap" }}>
                    {completed}/{total}
                </span>
            </div>
        </Tooltip>
    );
};

/**
 * 状态摘要标签组
 */
const StatusBadges = ({ statusSummary }) => {
    const { processing = 0, failed = 0, review = 0, completed = 0, voided = 0 } = statusSummary || {};
    return (
        <Space size={4} wrap>
            {processing > 0 && (
                <Tag icon={<SyncOutlined spin />} color="processing">
                    {processing}
                </Tag>
            )}
            {failed > 0 && (
                <Tag icon={<CloseCircleOutlined />} color="error">
                    {failed}
                </Tag>
            )}
            {review > 0 && (
                <Tag icon={<ExclamationCircleOutlined />} color="warning">
                    {review}
                </Tag>
            )}
            {completed > 0 && (
                <Tag icon={<CheckCircleOutlined />} color="success">
                    {completed}
                </Tag>
            )}
            {voided > 0 && (
                <Tag icon={<StopOutlined />} color="default">
                    {voided}
                </Tag>
            )}
        </Space>
    );
};

// ---- 主组件 ----

const ApInvBatchListPage = () => {

    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [batches, setBatches] = useState([]);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const [searchText, setSearchText] = useState("");
    const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);

    // 筛选条件（Drawer 内的结构化筛选，存储为 API 参数格式）
    // admin（超级管理员）默认看到所有人的数据，普通用户默认只看自己的
    const [filters, setFilters] = useState(() => ({
        ...(user?.id && !user?.is_superuser ? { created_by_user_id: user.id } : {}),
    }));

    // 当用户信息延迟加载完成时，补充默认的创建人筛选（仅非 admin）
    const creatorInitializedRef = useRef(!!user?.id);
    useEffect(() => {
        if (!creatorInitializedRef.current && user?.id) {
            creatorInitializedRef.current = true;
            if (!user.is_superuser) {
                setFilters((prev) => ({ ...prev, created_by_user_id: user.id }));
            }
        }
    }, [user]);

    // 供应商引用数据 — Page 是唯一所有者，Filter 通过 props 消费
    const [supplierMap, setSupplierMap] = useState({});       // id → 短名称（summary 翻译用）
    const [supplierOptions, setSupplierOptions] = useState([]); // [{ value, label }]（Select 下拉用）
    useEffect(() => {
        const loadSuppliers = async () => {
            try {
                const response = await contractManagementService.businessPartner.getSuppliers({
                    is_active: true,
                    page: 1,
                    page_size: 500,
                });
                if (response.data && Array.isArray(response.data.items)) {
                    const map = {};
                    const options = [];
                    response.data.items.forEach((s) => {
                        map[s.id] = s.code || (s.name.length > 8 ? s.name.substring(0, 8) + ".." : s.name);
                        options.push({
                            value: s.id,
                            label: s.code ? `${s.name} (${s.code})` : s.name,
                        });
                    });
                    setSupplierMap(map);
                    setSupplierOptions(options);
                }
            } catch (error) {
                console.error("加载供应商映射失败:", error);
            }
        };
        loadSuppliers();
    }, []);

    // 创建者用户列表 — 所有用户均可加载，供 Filter 下拉使用
    const [creatorOptions, setCreatorOptions] = useState([]); // [{ value, label }]
    useEffect(() => {
        const loadCreators = async () => {
            try {
                const res = await settlementApi.getBatchCreators();
                const creators = res.data || res || [];
                setCreatorOptions(
                    (Array.isArray(creators) ? creators : []).map((c) => ({
                        value: c.user_id,
                        label: c.full_name || c.username,
                    }))
                );
            } catch (error) {
                console.error("加载创建者列表失败:", error);
            }
        };
        loadCreators();
    }, []);

    // 快捷搜索防抖
    const searchDebounceRef = useRef(null);

    /** 筛选摘要项 -- 将 filters 翻译为用户可读标签 */
    const filterSummaryItems = useMemo(() => {
        const items = [];

        // 创建人
        if (filters.created_by_user_id) {
            const isMe = user && filters.created_by_user_id === user.id;
            items.push({ label: "创建人", value: isMe ? "我的 (默认)" : `用户 ${filters.created_by_user_id}` });
        }

        // 供应商
        if (filters.vendor_id != null) {
            const name = supplierMap[filters.vendor_id] || `ID:${filters.vendor_id}`;
            items.push({ label: "供应商", value: name });
        }

        // 费用类型
        if (filters.cost_type) {
            const label = STANDARD_COST_TYPE_LABELS[filters.cost_type.toUpperCase()] || filters.cost_type;
            items.push({ label: "费用类型", value: label });
        }

        // 机场
        if (filters.airport_code) {
            items.push({ label: "机场", value: filters.airport_code });
        }

        // 批次号搜索（hidden -- 已有 quickControls 展示，但仍计入筛选总数和 tooltip）
        if (searchText) {
            items.push({
                label: "搜索",
                value: searchText.length > 10 ? searchText.substring(0, 10) + "..." : searchText,
                hidden: true,
            });
        }

        return items;
    }, [filters, supplierMap, searchText, user]);

    /** 构造工作台跳转 URL（三元组参数） */
    const buildWorkbenchUrl = useCallback((record) => {
        const params = new URLSearchParams();
        params.set("batch", record.batch_number);
        if (record.vendor_id) params.set("vendor", record.vendor_id);
        if (record.standard_cost_type) params.set("costType", record.standard_cost_type);
        return `/settlement/ap-workbench?${params.toString()}`;
    }, []);

    // ---- 数据加载 ----
    const fetchBatches = useCallback(async (page = 1, pageSize = 20, search = "", extraFilters = {}) => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (search) params.search = search;
            // 合并结构化筛选参数
            Object.assign(params, extraFilters);
            const res = await settlementApi.listBatches(params);
            setBatches(res.data?.items || res.items || []);
            setTotal(res.data?.total || res.total || 0);
        } catch (err) {
            console.error("加载批次列表失败:", err);
            message.error("加载批次列表失败");
        } finally {
            setLoading(false);
        }
    }, []);

    // 响应式数据获取 — 监听 pagination、searchText、filters 的变化
    const prevDepsRef = useRef(null);
    useEffect(() => {
        const currentDeps = JSON.stringify({ p: pagination.current, ps: pagination.pageSize, s: searchText, f: filters });
        if (currentDeps !== prevDepsRef.current) {
            prevDepsRef.current = currentDeps;
            fetchBatches(pagination.current, pagination.pageSize, searchText, filters);
        }
    }, [pagination.current, pagination.pageSize, searchText, filters, fetchBatches]);

    // 快捷搜索 — 带防抖
    const handleQuickSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchText(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        // 触发防抖后的搜索（重置页码由 useEffect 自动处理）
        searchDebounceRef.current = setTimeout(() => {
            setPagination((prev) => ({ ...prev, current: 1 }));
        }, 500);
    }, []);

    const handleRefresh = useCallback(() => {
        fetchBatches(pagination.current, pagination.pageSize, searchText, filters);
    }, [pagination, searchText, filters, fetchBatches]);

    const handleTableChange = useCallback((pag) => {
        setPagination({ current: pag.current, pageSize: pag.pageSize });
    }, []);

    // ---- Drawer 筛选回调 ----
    // 所有用户均可通过 Drawer 管理 created_by_user_id（清空则查看全部）
    const handleFilterSearch = useCallback((filterParams) => {
        setFilters({ ...filterParams });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    // ---- 重置 ---- 恢复默认筛选（普通用户恢复为"我的"，管理员恢复为"全部"）
    const handleReset = useCallback(() => {
        setFilters(
            user?.id && !user?.is_superuser ? { created_by_user_id: user.id } : {}
        );
        setSearchText("");
        setPagination((prev) => ({ ...prev, current: 1 }));
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    }, [user]);

    // ---- 新建批次（上传抽屉完成后刷新列表） ----
    const handleUploadSuccess = useCallback(() => {
        setUploadDrawerOpen(false);
        fetchBatches(pagination.current, pagination.pageSize, searchText, filters);
    }, [fetchBatches, pagination, searchText, filters]);

    // 清理定时器
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, []);

    // ---- 表格列 ----
    const columns = useMemo(
        () => [
            {
                title: "批次号",
                dataIndex: "batch_number",
                key: "batch_number",
                width: 220,
                render: (text, record) => (
                    <a href={buildWorkbenchUrl(record)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        {text}
                    </a>
                ),
            },
            {
                title: "供应商",
                dataIndex: "vendor_name",
                key: "vendor_name",
                width: 150,
                ellipsis: true,
                render: (name) => name || <span style={{ color: "#999" }}>-</span>,
            },
            {
                title: "费用类型（预分类）",
                dataIndex: "standard_cost_type",
                key: "standard_cost_type",
                width: 120,
                render: (type) => (type ? <Tag>{getStandardCostTypeLabel(type)}</Tag> : <span style={{ color: "#999" }}>-</span>),
            },
            {
                title: "机场",
                dataIndex: "airport_code",
                key: "airport_code",
                width: 70,
                align: "center",
                render: (code) => (code ? <Tag color="blue">{code}</Tag> : <span style={{ color: "#999" }}>-</span>),
            },
            {
                title: "账单数",
                dataIndex: "ids_count",
                key: "ids_count",
                width: 110,
                align: "center",
                render: (count, record) => {
                    const voidedCount = record.voided_count || record.status_summary?.voided || 0;
                    return (
                        <span>
                            {count || 0}
                            {voidedCount > 0 && (
                                <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>
                                    (+{voidedCount}作废)
                                </Text>
                            )}
                        </span>
                    );
                },
            },
            {
                title: "处理进度",
                key: "progress",
                width: 150,
                render: (_, record) => <BatchProgressBar statusSummary={record.status_summary} total={record.ids_count} />,
            },
            {
                title: "状态分布",
                key: "status_badges",
                width: 180,
                render: (_, record) => <StatusBadges statusSummary={record.status_summary} />,
            },
            {
                title: "创建者",
                dataIndex: "created_by_full_name",
                key: "created_by",
                width: 100,
                ellipsis: true,
                render: (fullName, record) => {
                    const name = fullName || record.created_by_username || "系统导入";
                    return <span style={{ color: fullName ? undefined : "#999" }}>{name}</span>;
                },
            },
            {
                title: "最后更新",
                dataIndex: "updated_at",
                key: "updated_at",
                width: 160,
                render: (time) => (time ? new Date(time).toLocaleString() : "-"),
            },
        ],
        [buildWorkbenchUrl],
    );

    return (
        <div className="page-container">
            {/* 页面标题 + 操作按钮 */}
            <div className="page-header-compact">
                <div className="page-title-compact">账单批次上传</div>
                <Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setUploadDrawerOpen(true)}>
                        新建批次
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
                        刷新
                    </Button>
                </Space>
            </div>

            {/* 筛选栏 — 搜索框作为 quickControls，Drawer 内放结构化筛选 */}
            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="账单批次上传 - 筛选条件"
                quickControls={
                    <Input
                        placeholder="搜索批次号..."
                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                        size="small"
                        style={{ width: 220, height: 26 }}
                        value={searchText}
                        onChange={handleQuickSearchChange}
                        allowClear
                        onClear={() => {
                            setSearchText("");
                            setPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                    />
                }
            >
                <ApInvBatchFilter onSearch={handleFilterSearch} onReset={handleReset} loading={loading} supplierOptions={supplierOptions} creatorOptions={creatorOptions} currentUserId={user?.id} />
            </CollapsibleFilterBar>

            {/* 数据列表 */}
            <Card className="data-card">
                <Table
                    size="small"
                    rowKey="batch_key"
                    columns={columns}
                    dataSource={batches}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (t) => `共 ${t} 个批次`,
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1100 }}
                    onRow={(record) => ({
                        onClick: () => window.open(buildWorkbenchUrl(record), "_blank"),
                        style: { cursor: "pointer" },
                    })}
                    locale={{
                        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无批次，点击「新建批次」开始对账" />,
                    }}
                />
            </Card>

            {/* 新建批次 — 上传抽屉（完整模式：需填写全部配置） */}
            <UploadDrawer open={uploadDrawerOpen} onClose={() => setUploadDrawerOpen(false)} onSuccess={handleUploadSuccess} />
        </div>
    );
};

export default ApInvBatchListPage;
